import * as React from "react"
import { cn } from "../../utils/tailwindUtils"
import { fieldControlClass, textareaControlClass } from "./fieldStyles"
import { useState, useEffect, useCallback, useRef, forwardRef, useImperativeHandle } from "react";
import { Bold, Italic, Underline, List, ListOrdered, AlignLeft, AlignCenter, AlignRight, AlignJustify, RemoveFormatting } from "lucide-react";

const Input = React.forwardRef(({ className, type, ...props }, ref) => {
  return (
    <input
      type={type}
      className={cn(
        fieldControlClass(className),
        "file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground",
      )}
      ref={ref}
      {...props}
    />
  )
})

const Textarea = React.forwardRef(({
  className,
  rows = 4,
  resize = "vertical",
  showCharCount = false,
  maxLength,
  value = "", // Add value prop with default
  ...props
}, ref) => {
  const [charCount, setCharCount] = React.useState(value?.length || 0)

  // Update charCount when value prop changes
  React.useEffect(() => {
    setCharCount(value?.length || 0)
  }, [value])

  const handleChange = (e) => {
    const newValue = e.target.value
    setCharCount(newValue.length)

    if (props.onChange) {
      props.onChange(e)
    }
  }

  return (
    <div className="relative">
      <textarea
        className={cn(
          textareaControlClass(className),
          resize === "none" && "resize-none",
          resize === "vertical" && "resize-y",
          resize === "horizontal" && "resize-x",
          resize === "both" && "resize",
          showCharCount && maxLength && "pb-6",
        )}
        ref={ref}
        rows={rows}
        maxLength={maxLength}
        value={value} // Use value prop
        onChange={handleChange}
        {...props}
      />

      {showCharCount && maxLength && (
        <div className={cn(
          "absolute bottom-1 right-2 text-xs transition-colors duration-200",
          charCount > maxLength ? "text-red-500 dark:text-red-400" : "text-gray-500 dark:text-gray-400"
        )}>
          {charCount} / {maxLength}
        </div>
      )}
    </div>
  )
})

const EMPTY_CONTENT = "<p><br></p>";

export const CustomTextEditor = forwardRef(({ value, onChange, placeholder = "Start typing..." }, ref) => {
  const editorRef = useRef(null);
  const isProgrammaticallyUpdating = useRef(false);
  const [activeFormats, setActiveFormats] = useState({
    bold: false,
    italic: false,
    underline: false,
    insertOrderedList: false,
    insertUnorderedList: false,
    alignment: null
  });
  const [isFocused, setIsFocused] = useState(false);

  const showPlaceholder = !value || value === EMPTY_CONTENT;

  // Update toolbar state based on current selection
  const updateToolbarState = useCallback(() => {
    if (!editorRef.current) return;

    const newFormats = {
      bold: document.queryCommandState("bold"),
      italic: document.queryCommandState("italic"),
      underline: document.queryCommandState("underline"),
      insertOrderedList: document.queryCommandState("insertOrderedList"),
      insertUnorderedList: document.queryCommandState("insertUnorderedList"),
      alignment: null,
    };

    // Check alignment
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      let node = sel.anchorNode;
      while (node && node !== editorRef.current) {
        if (node.nodeType === 1) { // Element node
          const element = node;
          const textAlign = element.style.textAlign;
          if (textAlign === "left") newFormats.alignment = "justifyLeft";
          else if (textAlign === "center") newFormats.alignment = "justifyCenter";
          else if (textAlign === "right") newFormats.alignment = "justifyRight";
          else if (textAlign === "justify") newFormats.alignment = "justifyFull";

          if (newFormats.alignment) break;
        }
        node = node.parentNode;
      }
    }

    if (!newFormats.alignment) {
      newFormats.alignment = 'justifyLeft';
    }

    setActiveFormats(newFormats);
  }, []);

  // Initialize editor content - FIXED
  useEffect(() => {
    const editor = editorRef.current;
    if (editor && value !== undefined) {
      // Only update if the value is different from current content
      const currentContent = editor.innerHTML;
      const newContent = value || EMPTY_CONTENT;

      if (currentContent !== newContent) {
        isProgrammaticallyUpdating.current = true;
        editor.innerHTML = newContent;
        isProgrammaticallyUpdating.current = false;

        // Update toolbar state after content is set
        setTimeout(updateToolbarState, 10);
      }
    }
  }, [value, updateToolbarState]);

  // Listen for selection changes
  useEffect(() => {
    const handleSelectionChange = () => {
      updateToolbarState();
    };

    document.addEventListener("selectionchange", handleSelectionChange);
    return () => {
      document.removeEventListener("selectionchange", handleSelectionChange);
    };
  }, [updateToolbarState]);

  // Handle input changes
  const handleInput = useCallback((event) => {
    if (isProgrammaticallyUpdating.current) return;
    const newContent = event.currentTarget.innerHTML;
    onChange(newContent === EMPTY_CONTENT ? "" : newContent);
  }, [onChange]);

  // Enhanced list handling
  const handleListCommand = useCallback((command) => {
    if (!editorRef.current) return;

    // Focus the editor first
    editorRef.current.focus();

    // Save current selection
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    // Check if we're already in a list of the same type
    const isAlreadyInList = document.queryCommandState(command);

    if (isAlreadyInList) {
      // If already in list, remove the list formatting
      document.execCommand("insertUnorderedList", false);
      document.execCommand("insertOrderedList", false);
    } else {
      // Remove any existing list formatting first
      if (command === "insertUnorderedList") {
        document.execCommand("insertOrderedList", false);
      } else {
        document.execCommand("insertUnorderedList", false);
      }

      // Apply the new list
      document.execCommand(command, false);
    }

    // Update toolbar state immediately
    updateToolbarState();

    // Manually trigger input to update state
    const event = new Event('input', { bubbles: true, cancelable: true });
    editorRef.current.dispatchEvent(event);
  }, [updateToolbarState]);

  // Handle formatting commands
  const handleCommand = useCallback((command) => {
    if (!editorRef.current) return;

    // Focus the editor first
    editorRef.current.focus();

    // Handle list commands differently
    if (command === "insertUnorderedList" || command === "insertOrderedList") {
      handleListCommand(command);
      return;
    }

    // Execute other commands normally
    document.execCommand(command, false);

    // Update toolbar state immediately
    updateToolbarState();

    // Manually trigger input to update state
    const event = new Event('input', { bubbles: true, cancelable: true });
    editorRef.current.dispatchEvent(event);
  }, [updateToolbarState, handleListCommand]);

  // Handle keyboard shortcuts
  const handleKeyDown = useCallback((e) => {
    if ((e.metaKey || e.ctrlKey)) {
      switch (e.key.toLowerCase()) {
        case 'b':
          e.preventDefault();
          handleCommand('bold');
          break;
        case 'i':
          e.preventDefault();
          handleCommand('italic');
          break;
        case 'u':
          e.preventDefault();
          handleCommand('underline');
          break;
      }
    }

    // Handle Enter key in lists
    if (e.key === 'Enter') {
      const isInList = document.queryCommandState("insertUnorderedList") || document.queryCommandState("insertOrderedList");
      if (isInList) {
        // Let the browser handle list continuation naturally
        setTimeout(updateToolbarState, 10);
      }
    }

    // Handle Tab key in lists
    if (e.key === 'Tab') {
      const isInList = document.queryCommandState("insertUnorderedList") || document.queryCommandState("insertOrderedList");
      if (isInList) {
        e.preventDefault();
        // Indent list item
        document.execCommand('indent', false);
        setTimeout(updateToolbarState, 10);
      } else {
        e.preventDefault();
        // Insert spaces for tab
        document.execCommand('insertText', false, "    ");
      }
    }
  }, [handleCommand, updateToolbarState]);

  // Expose methods to parent
  useImperativeHandle(ref, () => ({
    focus: () => editorRef.current?.focus(),
    insertText: (text) => {
      if (editorRef.current) {
        editorRef.current.focus();
        document.execCommand('insertText', false, text);
        handleInput({ currentTarget: editorRef.current });
      }
    },
    getContent: () => editorRef.current?.innerHTML || "",
  }));

  // Toolbar buttons configuration
  const formattingButtons = [
    { command: "bold", icon: Bold, title: "Bold (Ctrl+B)", active: activeFormats.bold },
    { command: "italic", icon: Italic, title: "Italic (Ctrl+I)", active: activeFormats.italic },
    { command: "underline", icon: Underline, title: "Underline (Ctrl+U)", active: activeFormats.underline },
  ];

  const alignmentButtons = [
    { command: "justifyLeft", icon: AlignLeft, title: "Align Left", active: activeFormats.alignment === "justifyLeft" },
    { command: "justifyCenter", icon: AlignCenter, title: "Align Center", active: activeFormats.alignment === "justifyCenter" },
    { command: "justifyRight", icon: AlignRight, title: "Align Right", active: activeFormats.alignment === "justifyRight" },
    { command: "justifyFull", icon: AlignJustify, title: "Justify", active: activeFormats.alignment === "justifyFull" },
  ];

  const listButtons = [
    { command: "insertUnorderedList", icon: List, title: "Bullet List", active: activeFormats.insertUnorderedList },
    { command: "insertOrderedList", icon: ListOrdered, title: "Numbered List", active: activeFormats.insertOrderedList },
  ];

  const clearButton = { command: "removeFormat", icon: RemoveFormatting, title: "Clear Formatting" };

  return (
    <div className="border border-gray-300 dark:border-gray-600 rounded-md overflow-hidden bg-background">
      {/* Toolbar */}
      <div className="border-b border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 p-2">
        <div className="flex flex-wrap items-center gap-1">
          {/* Formatting Buttons */}
          <div className="flex items-center border-r border-gray-300 dark:border-gray-600 pr-2 mr-2">
            {formattingButtons.map(({ command, icon: Icon, title, active }) => (
              <ToolbarButton
                key={command}
                icon={Icon}
                title={title}
                onClick={() => handleCommand(command)}
                isActive={active}
              />
            ))}
          </div>

          {/* Alignment Buttons */}
          <div className="flex items-center border-r border-gray-300 dark:border-gray-600 pr-2 mr-2">
            {alignmentButtons.map(({ command, icon: Icon, title, active }) => (
              <ToolbarButton
                key={command}
                icon={Icon}
                title={title}
                onClick={() => handleCommand(command)}
                isActive={active}
              />
            ))}
          </div>

          {/* List Buttons */}
          <div className="flex items-center border-r border-gray-300 dark:border-gray-600 pr-2 mr-2">
            {listButtons.map(({ command, icon: Icon, title, active }) => (
              <ToolbarButton
                key={command}
                icon={Icon}
                title={title}
                onClick={() => handleCommand(command)}
                isActive={active}
              />
            ))}
          </div>

          {/* Clear Formatting Button */}
          <div className="flex items-center">
            <ToolbarButton
              icon={clearButton.icon}
              title={clearButton.title}
              onClick={() => handleCommand(clearButton.command)}
            />
          </div>
        </div>
      </div>

      {/* Editor Area */}
      <div className="relative">
        <div
          ref={editorRef}
          contentEditable
          onInput={handleInput}
          onKeyDown={handleKeyDown}
          onClick={updateToolbarState}
          onFocus={() => {
            setIsFocused(true);
            updateToolbarState();
          }}
          onBlur={() => {
            setIsFocused(false);
            updateToolbarState();
          }}
          className={cn(
            "min-h-[300px] p-4 outline-none transition-colors duration-200",
            "prose prose-sm max-w-none dark:prose-invert",
            isFocused && "ring-2 ring-sidebar-primary/20",
            "font-sans text-sm leading-relaxed whitespace-pre-wrap break-words",
            // Enhanced list styling that works for both ordered and unordered lists
            "[&>ul]:list-disc [&>ul]:list-inside [&>ul]:ml-4",
            "[&>ol]:list-decimal [&>ol]:list-inside [&>ol]:ml-4",
            "[&_ul]:list-disc [&_ul]:list-inside [&_ul]:ml-4",
            "[&_ol]:list-decimal [&_ol]:list-inside [&_ol]:ml-4",
            "[&_li]:my-1 [&_li]:pl-1"
          )}
          style={{
            lineHeight: '1.6',
            wordWrap: 'break-word'
          }}
          data-testid="text-editor"
          suppressContentEditableWarning={true}
        />
        {showPlaceholder && (
          <div className="absolute top-4 left-4 text-gray-400 dark:text-gray-500 pointer-events-none">
            {placeholder}
          </div>
        )}
      </div>
    </div>
  );
});

// Toolbar Button Component
const ToolbarButton = ({ icon: Icon, onClick, isActive, title }) => {
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseDown={(e) => e.preventDefault()}
      className={cn(
        "p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors",
        isActive && "bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300"
      )}
      title={title}
    >
      <Icon className="w-4 h-4" />
    </button>
  );
};

Input.displayName = "Input"
Textarea.displayName = "Textarea"
CustomTextEditor.displayName = 'CustomTextEditor';

export { Input, Textarea }